/**
 * Turning validation failures into something a form can render.
 *
 * Two sources produce them and both are handled the same way, because the
 * browser and the server run the *same* Zod schemas (spec section 14):
 *
 * - the shared schema, run in the browser before the request goes out;
 * - the server's `VALIDATION_ERROR` field map, run on the submission that
 *   actually arrived.
 *
 * Keys can be deeper than a rendered control — `additionalCategorySlugs.0`,
 * `attribution.utmSource` — so anything that does not name a field this form
 * renders collapses to a form-level message. A message stranded on a key with
 * no input would otherwise be invisible, and the visitor would be told to fix
 * something with nothing to fix it in.
 */

import type { ZodError } from 'zod';

/** One message per field key. */
export type FieldErrors = Record<string, string>;

/** The whole-form key the server uses for errors with no field path. */
export const FORM_ERROR_KEY = '_form';

export interface SplitErrors {
  /** Messages that belong to a control this form renders. */
  fieldErrors: FieldErrors;
  /** Messages with nowhere to go. Rendered in the summary only. */
  formErrors: string[];
}

/**
 * `additionalCategorySlugs.0` belongs to `additionalCategorySlugs`; anything
 * else that is not a rendered field belongs to the form.
 */
function toFieldKey(path: string, known: readonly string[]): string | null {
  if (known.includes(path)) return path;
  const head = path.split('.')[0];
  if (head !== undefined && known.includes(head)) return head;
  return null;
}

function split(entries: readonly [string, string][], known: readonly string[]): SplitErrors {
  const fieldErrors: FieldErrors = {};
  const formErrors: string[] = [];

  for (const [path, message] of entries) {
    const key = toFieldKey(path, known);
    if (key === null) {
      if (!formErrors.includes(message)) formErrors.push(message);
      continue;
    }
    // First message per field: one error per input is what a form can show.
    if (!(key in fieldErrors)) fieldErrors[key] = message;
  }

  return { fieldErrors, formErrors };
}

/** Client-side validation failures, from the shared schema. */
export function fromZodError(error: ZodError, known: readonly string[]): SplitErrors {
  return split(
    error.issues.map((issue) => [issue.path.join('.'), issue.message] as [string, string]),
    known,
  );
}

/** Server validation failures, from the spec section 14 field map. */
export function fromServerFields(
  fields: Record<string, string>,
  known: readonly string[],
): SplitErrors {
  return split(Object.entries(fields), known);
}

/** The field keys carrying an error, in the order the form renders them. */
export function orderedErrorKeys(
  fieldErrors: FieldErrors,
  order: readonly string[],
): readonly string[] {
  return order.filter((key) => fieldErrors[key] !== undefined);
}
